import type { Insight } from './insights';

export const sicherheitInsights: Insight[] = [
  // ───────────────────────── PHP2Shell ─────────────────────────
  {
    slug: 'php2shell-php-sicherheitsluecke',
    title: 'PHP2Shell: Was Webseitenbetreiber jetzt tun müssen',
    excerpt:
      'Zwei kritische Lücken in PHP selbst (CVE-2026-109618 und CVE-2026-109619) gefährden fast jede Website mit WordPress, Shop oder eigenem PHP-Code. Was passiert ist, wer betroffen ist, wer das Update einspielen muss – und was Sie heute konkret tun sollten.',
    seoTitle: 'PHP2Shell: Was Betreiber jetzt tun müssen',
    seoDescription:
      'PHP2Shell (CVE-2026-109618, CVE-2026-109619): Welche PHP-Versionen betroffen sind, welche Updates helfen, was bei PHP 8.1 und älter gilt – mit Checkliste & FAQ.',
    category: 'Sicherheit',
    readingTime: '8 Min.',
    datePublished: '2026-10-21',
    heroImage: '/images/ratgeber/php2shell-sicherheitsluecke',
    heroImageAlt: 'Techniker schließt einen Laptop an einen Server im Rechenzentrum an, ein Modul leuchtet orange als Warnsignal',
    author: { name: 'Jens Goldhammer', title: 'Technical Lead & KI-Experte' },
    tags: ['PHP2Shell', 'PHP Sicherheitslücke', 'CVE-2026-109618', 'CVE-2026-109619', 'WordPress Sicherheit', 'PHP Update', 'Webhosting'],
    contentBlocks: [
      {
        type: 'lead',
        text: 'Am 21. Oktober 2026 hat das PHP-Projekt Sicherheitsupdates für zwei kritische Lücken veröffentlicht. In der Fachwelt heißen sie „PHP2Shell“ – angelehnt an „Log4Shell“ von 2021. Betroffen ist nicht ein einzelnes Plugin, sondern PHP selbst: die Programmiersprache, auf der WordPress, WooCommerce, Joomla, TYPO3 und unzählige eigene Webanwendungen laufen.',
      },
      {
        type: 'paragraph',
        text: 'Hier fassen wir zusammen, was bekannt ist, ob Ihre Website betroffen ist, wer das Update einspielen muss und was Sie als Betreiberin oder Betreiber heute tun sollten – verständlich, ohne Fachchinesisch. **Stand dieses Artikels: 21. Oktober 2026, abends.** Die Lage entwickelt sich schnell; wir aktualisieren den Beitrag, wenn sich Wesentliches ändert.',
      },
      {
        type: 'callout',
        variant: 'warning',
        title: 'Kernpunkte',
        text: 'Praktisch jede Website mit PHP ist betroffen. Abhilfe schaffen nur die neuen Versionen **PHP 8.5.12, 8.4.27, 8.3.36 und 8.2.35**. Im normalen Webhosting spielt der Hoster das Update ein – Sie sollten aber prüfen, ob er es bestätigt hat. Wer noch PHP 8.1 oder älter nutzt, bekommt vom PHP-Projekt kein offizielles Update mehr und muss jetzt handeln. Nach dem Update gilt: auf Spuren eines Einbruchs prüfen.',
      },

      { type: 'heading', text: 'Was ist PHP2Shell und was ist passiert?' },
      {
        type: 'paragraph',
        text: 'Am 20. Oktober 2026 kündigte das PHP-Sicherheitsteam auf php.net ein Sicherheitsupdate für den Folgetag an. Beide Lücken sind nach dem Bewertungssystem CVSS 4.0 mit **9,2 von 10 Punkten** eingestuft. Noch bevor das Update erschien, gelangten erste Details an die Öffentlichkeit: Der Sicherheitsforscher Kevin Beaumont meldete, dass die Lücken vorab geleakt wurden, und Will Dormann wies auf einen Nachweis-Angriff (Proof of Concept) namens „php2shell“ hin, der gegen WordPress funktioniert.',
      },
      {
        type: 'paragraph',
        text: 'Laut CERT-EU, der IT-Sicherheitsstelle der EU-Institutionen, erschienen die Updates am **21. Oktober 2026 um 13:30 UTC (15:30 Uhr deutscher Zeit)** – etwas früher als ursprünglich angekündigt. Das BSI hat am selben Tag einen Sicherheitshinweis der zweithöchsten Stufe „Orange“ veröffentlicht: Maßnahmen sollen unverzüglich ergriffen werden.',
      },
      {
        type: 'table',
        headers: ['Kennung', 'Was steckt dahinter?', 'Bewertung'],
        rows: [
          ['CVE-2026-109618', 'Fehler bei der Behandlung von Speichermangel: PHP wird mitten im Einlesen einer Anfrage (z. B. eines Formulars mit Datei-Upload) abgebrochen und räumt danach unvollständige Daten falsch auf.', 'CVSS 9,2 – kritisch'],
          ['CVE-2026-109619', 'Veraltete interne Daten: Reste einer vorherigen Anfrage bleiben im Speicher und werden von der nächsten Anfrage desselben PHP-Prozesses weiterverwendet.', 'CVSS 9,2 – kritisch'],
        ],
        caption: 'Quelle: Sicherheitsmeldungen von php.net und CERT-EU vom 21.10.2026, vereinfacht übersetzt.',
      },
      {
        type: 'paragraph',
        text: 'Beide Lücken erlauben nach Angaben des PHP-Projekts, **ohne Login eigenen Code auf dem Webserver auszuführen**. Besonders brisant: Ein einziger präparierter Aufruf kann den Angriff auslösen, bevor WordPress, der Shop oder Ihre Anwendung überhaupt starten. Ein Sicherheits-Plugin innerhalb von WordPress kann deshalb nicht zuverlässig schützen.',
      },

      { type: 'heading', text: 'Warum ist die Lücke so gefährlich?' },
      {
        type: 'paragraph',
        text: 'Wer Code auf dem Server ausführen kann, hat die Website praktisch übernommen. Typische Folgen solcher Einbrüche sind:',
      },
      {
        type: 'list',
        items: [
          '**Datendiebstahl**: Kundendaten, Bestellungen, Formulareingaben oder Zugangsdaten werden ausgelesen.',
          '**Versteckte Hintertüren** („Web-Shells“), über die Angreifer später jederzeit wiederkommen.',
          '**Spam und Phishing** vom eigenen Server – mit dem Risiko, dass Ihre Domain auf Sperrlisten landet und E-Mails nicht mehr ankommen.',
          '**Umleitungen und Schadcode** auf der Website, die Besucher gefährden und Google-Rankings kosten.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Die Zeit drängt: Nach Einschätzung des BSI ist eine breite Ausnutzung innerhalb weniger Stunden nach Erscheinen der Patches wahrscheinlich. Laut dem Sicherheitsunternehmen Sansec hat Cloudflare bereits am Morgen des 21. Oktober erste Angriffsversuche beobachtet und Filterregeln für seine Kunden aktiviert. CERT-EU berichtet, dass erste Angriffs-Beispiele kursieren. Mehrere Organisationen, darunter Einrichtungen in den Niederlanden und einige Kommunen, haben ihre PHP-Websites vorsorglich vom Netz genommen.',
      },

      { type: 'heading', text: 'Welche Websites und PHP-Versionen sind betroffen?' },
      {
        type: 'paragraph',
        text: 'Kurz gesagt: **jede Website, bei der PHP Anfragen aus dem Internet verarbeitet.** Das gilt unabhängig davon, wie PHP auf dem Server eingebunden ist – PHP-FPM, Apache mit mod_php, php-cgi, FrankenPHP oder LiteSpeed. Das PHP-Projekt stuft Apache mit mod_php als besonders riskant ein.',
      },
      {
        type: 'table',
        headers: ['PHP-Version', 'Abgesichert ab', 'Status'],
        rows: [
          ['PHP 8.5', '8.5.12', 'Update verfügbar'],
          ['PHP 8.4', '8.4.27', 'Update verfügbar'],
          ['PHP 8.3', '8.3.36', 'Update verfügbar'],
          ['PHP 8.2', '8.2.35', 'Update verfügbar – Support endet am 31.12.2026'],
          ['PHP 8.1 und älter', '–', 'Kein offizielles Update; nur Notfall-Korrekturen im Quellcode bzw. über Linux-Distributionen'],
        ],
        caption: 'Stand 21.10.2026 laut php.net.',
      },
      {
        type: 'paragraph',
        text: 'Betroffen sind damit unter anderem WordPress und WooCommerce, Joomla, TYPO3, Drupal, Shopware, Magento, Nextcloud, Laravel-Anwendungen und Foren wie phpBB. Auch Geräte und Programme, die PHP intern nutzen – etwa Firewalls oder Router-Oberflächen –, können betroffen sein. Das niederländische NCSC sammelt dazu eine öffentliche Liste.',
      },
      {
        type: 'callout',
        variant: 'info',
        title: 'Nicht betroffen',
        text: 'Websites, die ohne PHP auskommen – etwa statische Seiten oder moderne JavaScript-Websites, wie auch unsere eigene Seite –, sind von PHP2Shell nicht betroffen. Gleiches gilt für reine Shopify-Shops, da Shopify kein PHP auf Ihrer Seite ausführt.',
      },

      { type: 'heading', text: 'Wer muss das Update einspielen – Sie oder Ihr Hoster?' },
      {
        type: 'paragraph',
        text: 'Für die meisten Betreiber ist das die wichtigste Frage. Die Antwort hängt davon ab, wie Ihre Website betrieben wird:',
      },
      {
        type: 'table',
        headers: ['Ihre Situation', 'Wer handeln muss'],
        rows: [
          ['Webhosting-Paket (z. B. IONOS, STRATO, all-inkl, mittwald) mit PHP 8.2 bis 8.5', 'Der Hoster aktualisiert PHP auf seinen Servern. Sie prüfen, ob er das bestätigt hat.'],
          ['Webhosting mit PHP 8.1 oder älter – auch mit gebuchtem „Extended Support“', 'Kein offizielles Update. Fragen Sie den Hoster schriftlich, ob er eine eigene Korrektur einspielt – sonst Umstieg auf eine aktuelle Version.'],
          ['Eigener Server oder VPS', 'Sie bzw. Ihr Dienstleister aktualisieren PHP über die Paketverwaltung (Debian, Ubuntu, RHEL u. a. haben die Korrekturen vorab erhalten).'],
          ['Docker-Container', 'Das Image muss mit der neuen PHP-Version neu gebaut und ausgerollt werden.'],
          ['Geräte mit eingebautem PHP', 'Der Hersteller muss ein Update liefern.'],
        ],
      },
      {
        type: 'callout',
        variant: 'tip',
        title: 'So finden Sie Ihre PHP-Version',
        text: 'Im Kundenbereich Ihres Hosters steht die PHP-Version meist unter „PHP-Einstellungen“ der Domain. In WordPress finden Sie sie unter **Werkzeuge → Website-Zustand → Bericht → Server**.',
      },
      {
        type: 'paragraph',
        text: 'Ein Sonderfall: Wer seine PHP-Pakete aus dem beliebten, ehrenamtlich gepflegten Remi-Repository bezieht, bekommt laut PHP-Projekt am Veröffentlichungstag noch keine Updates und sollte auf Pakete der Linux-Distribution umsteigen oder selbst kompilieren.',
      },

      { type: 'heading', text: 'Was gilt bei PHP 8.1, 7.4 und älteren Versionen?' },
      {
        type: 'paragraph',
        text: 'Hier liegt das größte Risiko. Das PHP-Projekt pflegt nur noch die Versionen 8.2 bis 8.5. Wegen der Schwere der Lücken hat das Sicherheitsteam zwar ausnahmsweise minimale Korrekturen im Quellcode für PHP 7.4, 8.0 und 8.1 veröffentlicht – die richten sich aber an Profis, die PHP selbst kompilieren. Ob Ihr Hoster sie einspielt, hängt allein von ihm ab.',
      },
      {
        type: 'paragraph',
        text: 'Der Umstieg auf eine aktuelle PHP-Version ist meist kein Klick im Kundenbereich: Ältere Themes, Plugins oder eigener Code laufen auf neuem PHP oft nicht mehr fehlerfrei. Ohne vorherigen Test bleibt die Seite womöglich weiß, oder Formulare funktionieren nicht mehr. Deshalb gilt: Umstieg jetzt planen, in einer Testumgebung prüfen – und bis dahin die Übergangsmaßnahmen weiter unten nutzen.',
      },

      { type: 'heading', text: 'Was hilft, bis das Update installiert ist?' },
      {
        type: 'paragraph',
        text: 'Das PHP-Projekt und CERT-EU sind hier deutlich: **Nur das Update schließt die Lücken vollständig.** Die folgenden Maßnahmen verkleinern die Angriffsfläche, machen eine Installation aber nicht sicher. Sie richten sich an Admins mit Zugriff auf die Serverkonfiguration:',
      },
      {
        type: 'list',
        items: [
          '**Speicherlimit**: memory_limit=-1 so setzen, dass Anwendungen es nicht überschreiben können (z. B. per php_admin_value im FPM-Pool) – und Speicher stattdessen über Container- oder cgroup-Grenzen begrenzen.',
          '**Kleine Anfragen**: post_max_size auf 8M senken, wenn keine Uploads nötig sind, sonst auf 32M; upload_max_filesize darunter halten.',
          '**Grenzen am Webserver oder Proxy** für Anfragegröße, Anzahl der Formularfelder und Upload-Teile setzen.',
          '**Upload-Fortschritt abschalten**: session.upload_progress.enabled=0 in der php.ini oder im FPM-Pool und alle PHP-Prozesse neu starten.',
          '**Unter PHP 8.5** zusätzlich fatal_error_backtraces=0 setzen.',
          '**Web Application Firewall** mit passenden Regeln vorschalten, etwa über Cloudflare.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Wo das Update nicht kurzfristig möglich ist, empfehlen BSI und CERT-EU, den Server vorübergehend vom Internet zu trennen. Für eine Firmenwebsite bedeutet das: lieber einen Tag eine Wartungsseite als eine gekaperte Seite, die wochenlang Spam verschickt.',
      },

      { type: 'heading', text: 'Checkliste: Was Webseitenbetreiber jetzt tun müssen' },
      {
        type: 'list',
        ordered: true,
        items: [
          '**PHP-Version herausfinden** – im Kundenbereich des Hosters oder in WordPress unter „Website-Zustand“.',
          '**Hoster-Status prüfen** – Statusseite oder E-Mail: Ist das Update auf 8.5.12, 8.4.27, 8.3.36 bzw. 8.2.35 bestätigt? Falls nicht: nachfragen.',
          '**Bei PHP 8.1 oder älter**: schriftlich beim Hoster nachfragen, ob eine Korrektur eingespielt wird, und den Umstieg auf eine aktuelle Version sofort planen.',
          '**Eigene Server und Container aktualisieren** – Pakete aktualisieren, alle PHP-Prozesse neu starten, Docker-Images neu bauen.',
          '**Übergangsmaßnahmen aktivieren**, falls das Update noch nicht möglich ist – oder die Seite vorübergehend offline nehmen.',
          '**Auf Spuren prüfen**: unbekannte PHP-Dateien (vor allem im Upload-Ordner), neue Admin-Konten, geänderte Theme- oder Plugin-Dateien, ungewöhnlicher E-Mail-Versand.',
          '**Zugangsdaten erneuern**, wenn die Seite längere Zeit ungeschützt war: Admin-Passwörter, Datenbank-Zugang, FTP/SFTP.',
          '**Backups sichern** – und prüfen, ob ein sauberer Stand von vor dem 20. Oktober vorhanden ist.',
        ],
      },
      {
        type: 'callout',
        variant: 'warning',
        title: 'Wichtig',
        text: 'Das Update schließt die Lücke – es entfernt aber keine Hintertür, die bereits eingebaut wurde. Wer nach dem Patch nicht auf Spuren prüft, kann trotz aktuellem PHP weiterhin kompromittiert sein.',
      },

      { type: 'heading', text: 'Warum Enno & Jens bei PHP2Shell helfen können' },
      {
        type: 'list',
        items: [
          '**Schnelle Einschätzung**: Wir prüfen, auf welcher PHP-Version Ihre Website läuft, wer sie betreibt und ob das Update angekommen ist.',
          '**Kontakt zum Hoster**: Wir klären mit Ihrem Hoster, ob und wann er die Korrektur einspielt – auch bei „Extended Support“.',
          '**Sicherer Umstieg**: Ältere WordPress- und WooCommerce-Seiten bringen wir in einer Testumgebung auf eine aktuelle PHP-Version, bevor live umgestellt wird.',
          '**Spurensuche**: Wir prüfen Dateien, Konten und Logs auf Anzeichen eines Einbruchs.',
          '**Langfristig sicher**: Mit unserem [Hosting & Betrieb](/leistungen/hosting-domains) übernehmen wir Updates und Monitoring dauerhaft.',
        ],
      },

      { type: 'heading', text: 'Fazit' },
      {
        type: 'paragraph',
        text: 'PHP2Shell ist eine der schwerwiegendsten Sicherheitslücken für Websites seit Jahren, weil sie nicht ein einzelnes Plugin, sondern das Fundament fast jeder klassischen Website betrifft. Für die meisten Betreiber ist die Lösung trotzdem überschaubar: PHP-Version prüfen, Update beim Hoster bestätigen lassen, danach auf Spuren schauen. Wirklich kritisch wird es für Seiten auf veraltetem PHP – hier sollte der Umstieg jetzt beginnen, nicht erst zum Jahresende.',
      },

      { type: 'heading', text: 'Häufige Fragen zu PHP2Shell' },
      {
        type: 'faq',
        items: [
          {
            question: 'Ist meine WordPress-Website von PHP2Shell betroffen?',
            answer:
              'Sehr wahrscheinlich ja, denn WordPress läuft auf PHP. Entscheidend ist, ob auf dem Server bereits PHP 8.5.12, 8.4.27, 8.3.36 oder 8.2.35 läuft. Ein aktuelles WordPress allein schützt nicht, weil der Angriff schon in PHP greift, bevor WordPress startet.',
          },
          {
            question: 'Hilft ein WordPress-Sicherheits-Plugin wie Wordfence?',
            answer:
              'Nur eingeschränkt. Ein Plugin läuft innerhalb von WordPress – der Angriff kann aber bereits beim Einlesen der Anfrage durch PHP stattfinden. Eine vorgeschaltete Web Application Firewall, etwa von Cloudflare oder dem Hoster, kann Angriffe abfangen. Ersetzen kann sie das PHP-Update nicht.',
          },
          {
            question: 'Muss ich bei einem normalen Webhosting-Paket selbst etwas tun?',
            answer:
              'In der Regel spielt der Hoster das Update auf seinen Servern ein. Sie sollten aber prüfen, ob er es bestätigt hat, und sicherstellen, dass Ihre Domain auf einer unterstützten PHP-Version (8.2 bis 8.5) läuft. Bei PHP 8.1 oder älter müssen Sie aktiv werden.',
          },
          {
            question: 'Was mache ich, wenn meine Seite noch auf PHP 7.4 oder 8.1 läuft?',
            answer:
              'Fragen Sie Ihren Hoster schriftlich, ob er eine Korrektur für Ihre Version einspielt. Planen Sie parallel den Umstieg auf eine aktuelle PHP-Version – am besten vorher in einer Testumgebung, damit Themes, Plugins und Formulare danach noch funktionieren.',
          },
          {
            question: 'Woran erkenne ich, ob meine Website schon gehackt wurde?',
            answer:
              'Typische Anzeichen sind unbekannte PHP-Dateien (vor allem in Upload-Ordnern), neue Administrator-Konten, geänderte Dateien in Theme oder Plugins, Weiterleitungen auf fremde Seiten oder auffällig viele versendete E-Mails. Auch Warnungen in der Google Search Console können ein Hinweis sein. Im Zweifel lassen Sie die Seite professionell prüfen.',
          },
          {
            question: 'Sind Shopify-Shops oder statische Websites betroffen?',
            answer:
              'Nein. Shopify führt auf Ihrem Shop kein PHP aus, und statische Websites oder moderne JavaScript-Websites ohne PHP-Server sind von PHP2Shell nicht betroffen. Betroffen sind WordPress, WooCommerce, Joomla, TYPO3, Shopware und andere PHP-basierte Systeme.',
          },
          {
            question: 'Sollte ich meine Website vorsorglich offline nehmen?',
            answer:
              'Wenn Ihr Hoster das Update bestätigt hat, ist das nicht nötig. Läuft die Seite aber auf einem eigenen Server ohne Update oder auf einer alten PHP-Version ohne Korrektur, empfehlen BSI und CERT-EU, sie bis zum Update vom Internet zu trennen – etwa mit einer Wartungsseite.',
          },
        ],
      },
      {
        type: 'cta',
        title: 'Unsicher, ob Ihre Website geschützt ist?',
        text: 'Wir prüfen kurzfristig, auf welcher PHP-Version Ihre Seite läuft, ob das Update angekommen ist und ob es Spuren eines Einbruchs gibt – verständlich erklärt und mit klaren nächsten Schritten.',
        label: 'Jetzt Kontakt aufnehmen',
        to: '/kontakt',
      },
      {
        type: 'paragraph',
        text: 'Langfristig keine Lust auf solche Tage? Mit unserem [Hosting & Betrieb](/leistungen/hosting-domains) kümmern wir uns um Updates, Monitoring und Backups – oder starten Sie mit unserem kostenlosen [Webseitencheck](/webseitencheck).',
      },
    ],
    sources: [
      { label: 'php.net – Security advisory: Critical remote code execution vulnerabilities, update now (21.10.2026)', url: 'https://www.php.net/archive/2026.php#2026-10-21-5' },
      { label: 'CERT-EU – Security Advisory 2026-018: Critical Vulnerabilities in PHP', url: 'https://cert.europa.eu/publications/security-advisories/2026-018/' },
      { label: 'Born’s Tech and Windows World – php2shell: PHP-Schwachstellen CVE-2026-109618 und CVE-2026-109619', url: 'https://borncity.com/blog/2026/10/21/php2shell-php-schwachstellen-cve-2026-109618-und-cve-2026-109619/' },
      { label: 'Patrick Hilker – PHP-Sicherheitslücke „PHP2Shell“: Ist meine Website betroffen?', url: 'https://patrickhilker.de/wissen/php-sicherheitsluecke-php2shell' },
      { label: 'Will Dormann (infosec.exchange) – Hinweis auf den Proof of Concept „php2shell“', url: 'https://infosec.exchange/@wdormann/117475540081194496' },
      { label: 'NCSC-NL – Übersicht betroffener Software mit eingebautem PHP', url: 'https://github.com/NCSC-NL/php2shell' },
    ],
    relatedServices: ['hosting-domains', 'webseitencheck', 'webseitenerstellung'],
  },
];
